'use client';

import { ReactNode, useEffect, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import TwoTilesFoldoutComponent from '@molecules/TwoTilesFoldoutComponent/TwoTilesFoldoutComponent';
import GridRow from '@molecules/GridRow/GridRow';
import GridCol from '@molecules/GridCol/GridCol';
import Link from '@atoms/Link/Link';
import useResponsiveCMSMedia from '@hooks/useResponsiveCMSMedia';
import Config from '@config';
import TwoTilesComponentSkeleton from '@molecules/TwoTilesComponent/TwoTilesComponent.skeleton';
import {
  StyledArrow,
  StyledButton,
  StyledHeading,
  StyledText,
  StyledTileWrapper,
  StyledTwoTilesWrapper,
} from './TwoTilesComponent.styles';
import { useAppSelector } from '@hooks/useAppDispatch';
import { selectSideNavMenuIsOpen } from '@slices/menuSlice';
import { selectMiniCartPreviewIsOpen } from '@slices/miniCartSlice';
import useResponsive from '@hooks/useResponsive';
import TrackedComponent from '@organisms/TrackedComponent/TrackedComponent';
import { useTranslations } from 'next-intl';
import Icon from '@atoms/Icon/Icon';
import AccordionDown from '@icons/accordion-down_small.svg';
import AccordionUp from '@icons/accordion-up_small.svg';
import useSymplifyExperiment from '@utility/ABTest/Symplify/useSymplifyExperiment';

interface Props {
  data: TwoTilesComponentType;
  logImpression?: () => void;
  onClick?: () => void;
}

const TwoTilesComponent = ({ data, logImpression, onClick }: Props) => {
  const [toggleProductList, setToggleProductList] = useState(false);
  const [animatingOpen, setAnimatingOpen] = useState(false);
  const showArrow = toggleProductList && !animatingOpen;
  const scrollAnchorRef = useRef<HTMLDivElement>(null);
  const { image } = useResponsiveCMSMedia({ media: data.media });
  const sideMenuIsOpen = useAppSelector(selectSideNavMenuIsOpen);
  const isCartPreviewOpen = useAppSelector(selectMiniCartPreviewIsOpen);
  const { isClient } = useResponsive();
  const t = useTranslations('common');
  const variant = useSymplifyExperiment('TwoTiles mix and match', 'Original');

  const { TOOLBAR_HEIGHT, MARGIN_HEIGHT } = {
    TOOLBAR_HEIGHT: 60,
    MARGIN_HEIGHT: 20,
  };
  const isMobile = useMediaQuery({
    query: Config.BREAKPOINTS.IS_MOBILE,
  });

  const onClickHandler = (actionType: TwoTilesActionType) => {
    if (actionType === 'PRODUCT_LIST') {
      const next = !toggleProductList;
      setToggleProductList(next);
      if (next) setAnimatingOpen(true);
    }
  };

  const createImageTile = (imageUrl: string, imageAlignment: string, actionType?: TwoTilesActionType) => {
    return (
      <StyledTileWrapper
        $first={imageAlignment === 'LEFT'}
        $clickable={!!data?.imageClickAction}
        onClick={() => onClickHandler(data?.imageClickAction)}
        $image={imageUrl}
        aria-label={actionType === 'PRODUCT_LIST' ? t('showProductListLabel') : undefined}
      />
    );
  };

  const tileButton = (
    <StyledButton
      $backgroundColor={data?.buttonColor}
      $textColor={data?.buttonTextColor}
      onClick={() => onClickHandler(data?.buttonAction)}
      aria-label={data?.buttonAction === 'PRODUCT_LIST' ? t('showProductListLabel') : undefined}
    >
      {data?.buttonText}
      {data?.buttonAction === 'PRODUCT_LIST' && variant === 'B' && (
        <>
          {' '}
          {toggleProductList ? (
            <Icon svg={AccordionUp} size={12} color="white" />
          ) : (
            <Icon svg={AccordionDown} size={12} color="white" />
          )}
        </>
      )}
    </StyledButton>
  );

  const wrapWithLink = (content: ReactNode, actionType: TwoTilesActionType): ReactNode => {
    if (actionType === 'PRODUCT_DETAIL') {
      if (!data?.product?.url) return content;
      return (
        <Link href={{ pathname: data.product.url }} shallow scroll={false} aria-label={t('openProductDetailLabel')}>
          {content}
        </Link>
      );
    }
    if (!data?.urlLink) return content;
    return (
      <Link href={data.urlLink} aria-label={t('showInfoLabel')}>
        {content}
      </Link>
    );
  };

  useEffect(() => {
    toggleProductList &&
      scrollAnchorRef.current &&
      window.scrollTo({
        top: scrollAnchorRef?.current?.offsetTop - (isMobile ? MARGIN_HEIGHT : TOOLBAR_HEIGHT + MARGIN_HEIGHT),
        behavior: 'smooth',
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toggleProductList]);

  return (
    <>
      {!data && <TwoTilesComponentSkeleton />}
      {data && (
        <StyledTwoTilesWrapper onClick={onClick}>
          <GridRow noGap>
            <GridCol size={{ startRow: { s: 2, m: 1 }, spanCol: { s: 4, m: 6 } }}>
              <StyledTileWrapper
                $backgroundColor={data?.backgroundColor}
                $first={data.imageAlignment !== 'LEFT'}
                $reducedPadding={isCartPreviewOpen && sideMenuIsOpen}
              >
                <StyledHeading $textColor={data?.titleTextColor} variant="h2">
                  {data?.title}
                </StyledHeading>
                <StyledText $textColor={data?.preambleTextColor} type="body">
                  {data?.preamble}
                </StyledText>
                {data?.buttonAction === 'URL_LINK' || data?.buttonAction === 'PRODUCT_DETAIL'
                  ? wrapWithLink(tileButton, data?.buttonAction)
                  : tileButton}
                {showArrow && <StyledArrow />}
              </StyledTileWrapper>
            </GridCol>
            <GridCol
              size={{
                startCol: { m: data.imageAlignment === 'LEFT' ? 1 : 7 },
                startRow: { s: 1 },
                spanCol: { s: 4, m: 6 },
              }}
            >
              {image.url && (data?.imageClickAction === 'URL_LINK' || data?.imageClickAction === 'PRODUCT_DETAIL')
                ? wrapWithLink(createImageTile(image.url, data.imageAlignment), data?.imageClickAction)
                : createImageTile(image.url, data.imageAlignment, data?.imageClickAction)}
            </GridCol>
          </GridRow>
          <div ref={scrollAnchorRef}>
            <TwoTilesFoldoutComponent
              data={data}
              show={toggleProductList}
              title={data.productListTitle}
              onClose={() => setToggleProductList(false)}
              onOpenAnimationEnd={() => setAnimatingOpen(false)}
            />
          </div>
        </StyledTwoTilesWrapper>
      )}
      {isClient && logImpression && <TrackedComponent onInView={logImpression} />}
    </>
  );
};

export default TwoTilesComponent;
